import React, { useState } from 'react';
import { colorSet } from '@components/Provider';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { Box, makeStyles } from '@material-ui/core';
import { useSelector } from '@store';
import { QuotationRequestForFixModal } from '@components/Form/ReceivedQuotationForm';
import ChatQuitModal from '@components/Chat/QuitModal';
import { useHistory } from 'react-router-dom';
import { isClosedTypes } from '@components/Chat/types';
import { VerificationDialog } from '@routes/MyAccount/components/Dialog/RequestActionDialog';
import {
  CapaQuotationUserVerificationType,
  useFetchMyQuotationUserLazyQuery
} from '@generated/graphql';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      width: 230,
      borderRadius: 10,
      backgroundColor: colorSet.gray000
    },
    header: {
      borderRadius: '10px 10px 0px 0px',
      padding: '10px 16px',
      color: colorSet.gray000,
      backgroundColor: colorSet.primary500
    },
    contentWrapper: {
      padding: '0px 16px 16px 16px'
    },
    content: {
      paddingTop: 16,
      borderTop: `1px solid ${colorSet.gray200}`
    },
    wordBreak: {
      wordBreak: 'break-all',
      '& b': {
        wordBreak: 'break-all'
      }
    },
    cta: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: 16,
      '& .mb-10': {
        marginBottom: 10
      },
      '& .mb-24': {
        marginBottom: 24
      },
      '& .text': {
        ...theme.typography.body2,
        color: colorSet.gray700,
        textDecoration: 'underline'
      }
    }
  };
});

const InducePurchaseOrderIssueBubble: React.FC = () => {
  const classes = useStyles();
  const rfqId = useSelector((state) => state.chat.rfqId);
  const partnerId = useSelector((state) => state.chat.quotationPartnerId);
  const chatRoomStatus = useSelector((state) => state.chat.chatRoomStatus);
  const hasPurchaseOrder = useSelector(
    (state) => state.chat.hasPurchaseOrder ?? false
  );
  const menuStatus = useSelector(
    (state) => state.chat.menuStatus?.purchaseOrder
  );
  const [fetchMe] = useFetchMyQuotationUserLazyQuery({
    fetchPolicy: 'cache-first'
  });

  const isClosed = chatRoomStatus
    ? isClosedTypes.includes(chatRoomStatus)
    : false;

  const history = useHistory();
  const [modal, setModal] = useState<'fix' | 'close' | 'verification' | null>(
    null
  );

  const create = async () => {
    const { data } = await fetchMe();
    const me = data?.fetchMyQuotationUser.data;
    const isVerified =
      me?.verificationType === CapaQuotationUserVerificationType.Verified;

    if (!rfqId || !partnerId) return;

    if (isVerified) {
      history.push(
        history.location.pathname + `?menu=create_purchaseorder&p=2`
      );
    } else {
      setModal('verification');
    }
  };

  const handleModal = (open: 'fix' | 'close' | null) => {
    setModal(open);
  };

  return (
    <>
      {modal === 'close' && (
        <ChatQuitModal open={true} onClose={() => handleModal(null)} />
      )}

      {modal === 'fix' && (
        <QuotationRequestForFixModal
          matches={false}
          open={true}
          onClose={() => handleModal(null)}
          rfqId={rfqId ?? undefined}
          partnerId={partnerId ?? undefined}
          hasProgressingPayment={hasPurchaseOrder}
        />
      )}

      {modal === 'verification' && (
        <VerificationDialog open={true} onClose={() => setModal(null)} />
      )}

      <Box className={classes.root}>
        <div className={classes.header}>
          <CustomizedTypography
            variant="body2"
            fontWeight="bold"
            color="inherit"
          >
            발주서
          </CustomizedTypography>
        </div>
        <div className={classes.contentWrapper}>
          <div className={classes.content}>
            <CustomizedTypography
              variant="body1"
              fontWeight="bold"
              gutterBottoms={10}
            >
              발주서 발행
            </CustomizedTypography>
            <CustomizedTypography variant="body2" className={classes.wordBreak}>
              견적 상담을 진행하셨다면 발주서를 발행하세요. 견적 수정이
              필요하다면 수정 요청을 하고 조건이 합의되었다면 바로 발주하세요.
            </CustomizedTypography>
          </div>
        </div>

        <div className={classes.cta}>
          <CustomizedButton
            className="mb-10"
            disabled={isClosed || menuStatus || hasPurchaseOrder}
            fullWidth
            variant="contained"
            color="primary"
            onClick={create}
          >
            <i className="request-gtm-makeOrder-btn">발주서 발행하기</i>
          </CustomizedButton>
          <CustomizedButton
            disabled={isClosed || hasPurchaseOrder}
            fullWidth
            variant="contained"
            color="default"
            className="mb-24"
            onClick={() => handleModal('fix')}
          >
            견적서 수정요청
          </CustomizedButton>
          <CustomizedButton
            disabled={isClosed || hasPurchaseOrder}
            fullWidth
            variant="text"
            className="text"
            color="default"
            onClick={() => handleModal('close')}
          >
            거래를 진행하지 않을래요
          </CustomizedButton>
        </div>
      </Box>
    </>
  );
};

export default InducePurchaseOrderIssueBubble;
